import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  shadow,
  spacing,
  status,
  transition,
  typography,
  layout,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { FAQ_CONTENT } from './legal-content.generated';

/**
 * The FAQ from lobbero.com/faq, rendered from data.
 *
 * On the web, the search box and the topic filters run from a page <script>.
 * Here the same behaviour is React state over the questions that
 * scripts/sync-legal.mjs parsed out of the web source. It matches the web:
 * - a topic filter shows that topic's questions, all closed;
 * - a search opens every match, so the matching words are on screen;
 * - no match shows the "ask us directly" line.
 */

const Wrap = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: ${spacing.xxl} ${spacing.xl} ${spacing.xxxl};
  font-family: ${typography.ui};
  color: ${palette.ink};

  ${down('sm')} {
    padding: ${spacing.lg} ${spacing.md} ${spacing.xxl};
  }
`;

const Hero = styled.div`
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0 0 14px;
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${palette.lagoon};
`;

const Title = styled.h1`
  margin: 0 0 18px;
  font-family: ${typography.display};
  font-weight: ${typography.weight.bold};
  font-size: clamp(2rem, 5vw, 2.9rem);
  line-height: ${typography.leading.tight};
  letter-spacing: ${typography.tracking.tight};
  text-wrap: balance;
`;

const Lede = styled.p`
  margin: 0 auto;
  max-width: 54ch;
  font-size: ${typography.size.md}px;
  line-height: 1.6;
  color: ${palette.slate};
`;

const Controls = styled.div`
  margin: 36px 0 8px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  align-items: center;
`;

const Search = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;

  & svg {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: ${palette.slateLight};
    pointer-events: none;
  }

  & input {
    width: 100%;
    box-sizing: border-box;
    min-height: 48px;
    padding: 13px 14px 13px 44px;
    /* 16px, or iOS zooms the page on focus and never zooms back. */
    font-family: ${typography.ui};
    font-size: 16px;
    color: ${palette.ink};
    background: ${palette.surface};
    border: 1.5px solid ${palette.lineStrong};
    border-radius: ${radius.sm};
    -webkit-appearance: none;
    appearance: none;
    transition: ${transition.colors};
  }

  & input::placeholder {
    color: ${palette.slateLight};
  }

  & input:focus {
    outline: none;
    border-color: ${palette.iris};
    box-shadow: ${shadow.focus};
  }
`;

const SrOnly = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing.xs};

  & button {
    min-height: ${layout.touchTarget};
    padding: 0 14px;
    border-radius: ${radius.pill};
    border: 1.5px solid ${palette.line};
    background: ${palette.surface};
    color: ${palette.slate};
    font-family: ${typography.mono};
    font-size: 12.5px;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
    transition: ${transition.colors};
  }

  & button:hover {
    color: ${palette.ink};
    border-color: ${palette.lineStrong};
  }

  & button:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  & button[aria-pressed='true'] {
    background: ${palette.lagoon};
    border-color: ${palette.lagoon};
    color: ${palette.white};
  }
`;

const List = styled.div`
  margin-top: 30px;
  border-top: 1px solid ${palette.line};

  & details {
    border-bottom: 1px solid ${palette.line};
  }

  & summary {
    list-style: none;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    gap: ${spacing.sm};
    min-height: ${layout.touchTarget};
    padding: 19px 4px;
    font-size: 1.1rem;
    font-weight: ${typography.weight.medium};
    letter-spacing: -0.005em;
    color: ${palette.ink};
  }

  & summary::-webkit-details-marker {
    display: none;
  }

  & summary::marker {
    content: '';
  }

  & summary:hover {
    color: ${palette.lagoon};
  }

  & summary:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
    border-radius: 4px;
  }

  & .cat {
    flex: 0 0 auto;
    min-width: 66px;
    margin-top: 4px;
    padding: 4px 7px;
    border-radius: 5px;
    background: ${palette.irisTint};
    color: ${status.brand.fg};
    font-family: ${typography.mono};
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-align: center;
  }

  & .chev {
    flex: 0 0 auto;
    margin-left: auto;
    width: 22px;
    height: 22px;
    color: ${palette.iris};
    transition: transform ${transition.base};
  }

  & details[open] .chev {
    transform: rotate(45deg);
  }

  @media (prefers-reduced-motion: reduce) {
    & .chev {
      transition: none;
    }
  }

  & .ans {
    max-width: 68ch;
    padding: 0 4px 22px 86px;
    font-size: 15.5px;
    line-height: 1.62;
    color: ${palette.slate};
  }

  & .ans a {
    color: ${palette.lagoon};
    font-weight: ${typography.weight.medium};
  }

  & .ans strong {
    color: ${palette.ink};
    font-weight: ${typography.weight.semibold};
  }

  ${down('sm')} {
    & .cat {
      display: none;
    }

    & .ans {
      padding-left: 4px;
    }

    & summary {
      font-size: 1.02rem;
      gap: 12px;
    }
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 44px 0;
  text-align: center;
  font-size: 15px;
  color: ${palette.slate};

  & a {
    color: ${palette.lagoon};
  }
`;

const matches = (item: (typeof FAQ_CONTENT.items)[number], q: string) =>
  !q ||
  `${item.question} ${item.answerHtml.replace(/<[^>]+>/g, '')}`
    .toLowerCase()
    .includes(q);

export const FaqBody: React.FC = () => {
  const [topic, setTopic] = useState('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  const q = query.trim().toLowerCase();

  const shown = useMemo(
    () =>
      FAQ_CONTENT.items
        .map((item, index) => ({ item, index }))
        .filter(
          ({ item }) =>
            (topic === 'all' || item.topic === topic) && matches(item, q)
        ),
    [topic, q]
  );

  const pickTopic = (id: string) => {
    setTopic(id);
    // A topic filter does not open anything, or "Pricing" would expand five
    // answers at once.
    if (!q) setOpen(new Set());
  };

  const onSearch = (value: string) => {
    setQuery(value);
    // A search opens every match, so the matching words are on screen.
    const nextQ = value.trim().toLowerCase();
    setOpen(
      nextQ
        ? new Set(
            FAQ_CONTENT.items
              .map((item, index) => (matches(item, nextQ) ? index : -1))
              .filter(index => index >= 0)
          )
        : new Set()
    );
  };

  const onToggle = (index: number, isOpen: boolean) =>
    setOpen(previous => {
      if (previous.has(index) === isOpen) return previous;
      const next = new Set(previous);
      if (isOpen) next.add(index);
      else next.delete(index);

      return next;
    });

  return (
    <Wrap>
      <Hero>
        <Eyebrow>{FAQ_CONTENT.eyebrow}</Eyebrow>
        <Title>{FAQ_CONTENT.title}</Title>
        <Lede>{FAQ_CONTENT.lede}</Lede>
      </Hero>

      <Controls>
        <Search>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <SrOnly htmlFor="faq-search">Search questions</SrOnly>
          <input
            id="faq-search"
            type="search"
            placeholder="Search questions…"
            autoComplete="off"
            value={query}
            onChange={event => onSearch(event.target.value)}
          />
        </Search>
        <Filters role="group" aria-label="Filter by topic">
          {FAQ_CONTENT.topics.map(t => (
            <button
              key={t.id}
              type="button"
              aria-pressed={topic === t.id}
              onClick={() => pickTopic(t.id)}
            >
              {t.label}
            </button>
          ))}
        </Filters>
      </Controls>

      {shown.length ? (
        <List>
          {shown.map(({ item, index }) => (
            <details
              key={index}
              open={open.has(index)}
              onToggle={event =>
                onToggle(index, (event.currentTarget as HTMLDetailsElement).open)
              }
            >
              <summary>
                <span className="cat">{item.topicLabel}</span>
                <span>{item.question}</span>
                <svg
                  className="chev"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </summary>
              {/* Build-time content from lobbero_web; scripts/sync-legal.mjs
                  refuses a script, an inline handler or a javascript: URL. */}
              <div
                className="ans"
                dangerouslySetInnerHTML={{ __html: item.answerHtml }}
              />
            </details>
          ))}
        </List>
      ) : (
        <Empty role="status">
          No questions match that search. Try a different term, or{' '}
          <a href="mailto:hello@lobbero.com">ask us directly</a>.
        </Empty>
      )}
    </Wrap>
  );
};
